import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import { ExerciseLogs, ResolvedGroup } from "../../helpers/exerciseSelectors";
import { EXERCISE_GROUP_TYPE } from "../../model/workoutEnums";
import ExerciseCard from "./ExerciseCard";

type Props = {
  group: ResolvedGroup;
  groupIndex: number;
  /** The manual session's logged sets, when the workout was logged. */
  logs: ExerciseLogs | null;
  /** Whether the workout has a merged session (mobile `isCompleted`). */
  isLogged: boolean;
};

/**
 * One exercise group, after the mobile `WorkoutV2GroupElement`: a plain
 * (`None`) group lists its exercises bare; EMOM / Superset / RFT / Circuit
 * groups frame them under the group's name and its rule.
 */
const ExerciseGroupSection = ({ group, groupIndex, logs, isLogged }: Props) => {
  const theme: Theme = useTheme();

  const exercises = group.exercises.map((exercise, exerciseIndex) => (
    <ExerciseCard
      key={exercise.id}
      exercise={exercise}
      group={group}
      groupIndex={groupIndex}
      exerciseIndex={exerciseIndex}
      logs={logs}
      isLogged={isLogged}
    />
  ));

  if (group.type === EXERCISE_GROUP_TYPE.None) {
    return <Box sx={styles.list}>{exercises}</Box>;
  }

  return (
    <Box sx={styles.frame(theme)}>
      <Box sx={styles.header}>
        <Box sx={styles.title(theme)}>{group.title}</Box>
        {group.description && (
          <Box sx={styles.description(theme)}>{group.description}</Box>
        )}
      </Box>
      <Box sx={styles.list}>{exercises}</Box>
    </Box>
  );
};

export default ExerciseGroupSection;

const styles = {
  frame: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
    padding: "12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colorTokens.N[10],
  }),
  header: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  description: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  list: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
  },
};
